/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, Typography, TableContainer, TableRow, TableCell, Table, TableHead, TableBody, IconButton, TextField, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';

import { useTranslation } from 'react-i18next';
import { Add, Delete} from '@mui/icons-material';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { IApproverDocumentGuiModel } from '../../../model/IApproverGuiModel';
import { IDistributionGuiModel } from '../../../model/IDistributionGuiModel';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';
import { NumericFormatWhole } from '../../../common/CustomFunctions';
import DialogAccountSearch from './DialogAccountSearch';
import { IVatGuiModel } from '../../../model/IVatGuiModel';
import { IDepartmentGuiModel } from '../../../model/IDepartmentGuiModel';
import { IProjectGuiModel } from '../../../model/IProjectGuiModel';
import DialogVatCodeSearch from './DialogVatCodeSearch';
import DialogDepartmentSearch from './DialogDepartmentSearch';
import DialogProjectSearch from './DialogProjectSearch';

interface IApproverDistributions {
    document: IApproverDocumentGuiModel | undefined,
    setDocument: Dispatch<SetStateAction<IApproverDocumentGuiModel | undefined>>,
    heightLg: string,
    isSmAndUpScreen?: boolean,
    organizationId: string | undefined,
    onlyReasign?: boolean
}
interface IApproverDistribution {
    document: IApproverDocumentGuiModel | undefined,
    setDocument: Dispatch<SetStateAction<IApproverDocumentGuiModel | undefined>>,
    index: number,
    organizationId: string | undefined,
    onlyReasign?: boolean
}

const SubRows = (props: IApproverDistribution): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const { document, setDocument, index } = props;

    let distributions = document?.lines;

    const [account, setAccount] = useState((distributions && distributions[index].account) ? distributions[index].account : undefined);
    const [vat, setVat] = useState<string>((distributions && distributions[index].vatId) ? (distributions[index].vatId ?? "") : "");
    const [amount, setAmount] = useState<string>((distributions && distributions[index].amount) ? (distributions[index].amount ?? "") : "");
    const [department, setDepartment] = useState<string>((distributions && distributions[index].departmentId) ? (distributions[index].departmentId ?? "") : "");
    const [project, setProject] = useState<string>((distributions && distributions[index].projectId) ? (distributions[index].projectId ?? "") : "");

    const [openAccountDialog, setOpenAccountDialog] = useState(false);
    const handleCloseAccountDialog = () => {
        setOpenAccountDialog(false);
    }

    function handleDeleteDistribution(distributionIndex: number) {
        if (document && distributions) {
            let newDistributions: IDistributionGuiModel[] = distributions.filter((e, index) => index !== distributionIndex);
            let updatedDocument: IApproverDocumentGuiModel = {

                id: document.id,
                amount: document.amount,
                invoiceNo: document.invoiceNo,
                voucherDate: document.voucherDate,
                dueDate: document.dueDate,
                cid: document.cid,
                supplierId: document.supplierId,
                supplierName: document.supplierName,
                assignedUserId: document.assignedUserId,
                assignedUserFullName: document.assignedUserFullName,
                comment: document.comment,
                status: document.status,
                type: document.type,
                lines: newDistributions,
                requests: document.requests,
                files: document.files,
            }
            setDocument(updatedDocument);
        }
    }
    function handleUpdateDistribution(distributionIndex: number, account: IAccountGuiModel | null, vatId: string | null, amount: string | null, departmentId: string | null, projectId: string | null) {
        if (document && distributions) {
            let newDistribution: IDistributionGuiModel = {
                account: account ? account : distributions[distributionIndex].account,
                vatId: vatId ? vatId : distributions[distributionIndex].vatId,
                amount: amount ? amount : distributions[distributionIndex].amount,
                projectId: projectId ? projectId : distributions[distributionIndex].projectId,
                departmentId: departmentId ? departmentId : distributions[distributionIndex].departmentId,
            };
            let newDistributions = distributions.map((e, index) => {
                if (index === distributionIndex) {
                    return newDistribution;
                } else {
                    return e;
                }
            });
            let updatedDocument: IApproverDocumentGuiModel = {

                id: document.id,
                amount: document.amount,
                invoiceNo: document.invoiceNo,
                voucherDate: document.voucherDate,
                dueDate: document.dueDate,
                cid: document.cid,
                supplierId: document.supplierId,
                supplierName: document.supplierName,
                assignedUserId: document.assignedUserId,
                assignedUserFullName: document.assignedUserFullName,
                comment: document.comment,
                status: document.status,
                type: document.type,
                lines: newDistributions,
                requests: document.requests,
                files: document.files,
            }
            setDocument(updatedDocument);
        }
    }
    function amountError() {
        if (amount && amount !== "" && !/^\d+(,\d+)?$/.test(amount) ) {
            return true;
        } else {
            return false;
        }
    }

    const [dialogAccountOpen, setDialogAccountOpen] = useState(false);
    const handleAccountClick = () => {
        setDialogAccountOpen(true);
    }
    const handleAccountClose = () => {
        setDialogAccountOpen(false);
    }
    const handleNewAccount = (newAccount: IAccountGuiModel) => {
        handleUpdateDistribution(index, newAccount, null, null, null, null)
    }
    //##
    const [dialogVatOpen, setDialogVatOpen] = useState(false);
    const handleVatClick = () => {
        setDialogVatOpen(true);
    }
    const handleVatClose = () => {
        setDialogVatOpen(false);
    }
    const handleNewVat = (newVat: IVatGuiModel) => {
        handleUpdateDistribution(index, null, newVat.vatCodeId, null, null, null)
    }
    //##
    const [dialogDepartmentOpen, setDialogDepartmentOpen] = useState(false);
    const handleDepartmentClick = () => {
        setDialogDepartmentOpen(true);
    }
    const handleDepartmentClose = () => {
        setDialogDepartmentOpen(false);
    }
    const handleNewDepartment = (newDepartment: IDepartmentGuiModel) => {
        handleUpdateDistribution(index, null, null, null, newDepartment.departmentId, null)
    }
    //##
    const [dialogProjectOpen, setDialogProjectOpen] = useState(false);
    const handleProjectClick = () => {
        setDialogProjectOpen(true);
    }
    const handleProjectClose = () => {
        setDialogProjectOpen(false);
    }
    const handleNewProject = (newProject: IProjectGuiModel) => {
        handleUpdateDistribution(index, null, null, null, null, newProject.projectId)
    }

    return <TableRow>
        <TableCell sx={{ paddingLeft: theme.spacing(3), paddingRight: theme.spacing(0.5), }}>
            <TextField
                size="small"
                variant="filled"
                color="warning"
                value={account ? account.accountId + " - " + account.accountType : ""}
                label={account ? account.name : "" }
                disabled={props.onlyReasign}
                autoComplete="off"
                onChange={undefined}
                onClick={handleAccountClick}
                sx={{ minWidth: "6.5rem", "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper,} }}
                id="debitId" />
            <DialogAccountSearch
                organizationId={props.organizationId}
                dialogOpen={dialogAccountOpen}
                handleDialogClose={handleAccountClose}
                setAccount={setAccount}
                handleNewAccount={handleNewAccount} />
        </TableCell>
        <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
            <TextField
                size="small"
                variant="filled"
                disabled={props.onlyReasign}
                autoComplete="off"
                value={vat}
                onClick={handleVatClick}
                onChange={undefined}
                sx={{
                    minWidth: "6.5rem",
                    "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper }
                }}
                InputProps={{ inputComponent: NumericFormatWhole as any }}
                id="vatId" />
            <DialogVatCodeSearch
                organizationId={props.organizationId}
                dialogOpen={dialogVatOpen}
                handleDialogClose={handleVatClose}
                setVatCode={setVat}
                handleNewVatCode={handleNewVat}/>
        </TableCell>
        <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
            <TextField
                size="small"
                variant="filled"
                disabled={props.onlyReasign}
                error={amountError()}
                value={amount}
                onChange={(e) => {
                    setAmount(e.target.value);
                    handleUpdateDistribution(index, null, null, e.target.value, null, null);
                }}
                inputProps={{ style: {textAlign:"right"} }}
                sx={{
                    minWidth: "6.5rem",
                    "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper },
                    border:amountError() ? 2 : 0, borderColor: "red"
                }}
                //InputProps={{ inputComponent: NumericFormatWhole as any }}
                id="amountId" />
        </TableCell>
        <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
            <TextField
                size="small"
                variant="filled"
                disabled={props.onlyReasign}
                autoComplete="off"
                color="warning"
                value={department}
                onClick={handleDepartmentClick}
                onChange={undefined}
                sx={{ minWidth: "6.5rem", "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper } }}
                id="departmentId" />
            <DialogDepartmentSearch
                organizationId={props.organizationId}
                dialogOpen={dialogDepartmentOpen}
                handleDialogClose={handleDepartmentClose}
                setDepartment={setDepartment}
                handleNewDepartment={handleNewDepartment} />
        </TableCell>
        <TableCell sx={{ paddingLeft: theme.spacing(0.5)}}>
            <TextField
                size="small"
                variant="filled"
                disabled={props.onlyReasign}
                autoComplete="off"
                color="warning"
                value={project}
                onClick={handleProjectClick}
                onChange={undefined}
                sx={{ minWidth: "6.5rem", "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper } }}
                id="projectId" />
            <DialogProjectSearch
                organizationId={props.organizationId}
                dialogOpen={dialogProjectOpen}
                handleDialogClose={handleProjectClose}
                setProject={setProject}
                handleNewProject={handleNewProject} />
        </TableCell>
        <TableCell padding="none">
            <IconButton onClick={() => handleDeleteDistribution(index)}>
                <Delete />
            </IconButton>
        </TableCell>
    </TableRow>
}

const DialogDocumentApproverDistributions = (props: IApproverDistributions): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const { isSmAndUpScreen, document, setDocument, heightLg } = props;

    let distributions = document?.lines;

    function handleAddDistribution() {
        if (document && distributions) {

            let newDistributions = [...distributions];
            let newDistribution: IDistributionGuiModel = {
                account: undefined,
                vatId: undefined,
                amount: undefined,
                projectId: undefined,
                departmentId: undefined,
            };
            newDistributions.push(newDistribution);
            let updatedDocument: IApproverDocumentGuiModel = {

                id: document.id,
                amount: document.amount,
                invoiceNo: document.invoiceNo,
                voucherDate: document.voucherDate,
                dueDate: document.dueDate,
                cid: document.cid,
                supplierId: document.supplierId,
                supplierName: document.supplierName,
                assignedUserId: document.assignedUserId,
                assignedUserFullName: document.assignedUserFullName,
                comment: document.comment,
                status: document.status,
                type: document.type,
                lines: newDistributions,
                requests: document.requests,
                files: document.files,
            }
            setDocument(updatedDocument);
        }
    }
    return <Grid container component={Paper} direction="column"
        sx={{
            //border: 1,
            //borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.grey[100],
            width: "100%",
            overflowX: "auto",
        }}>
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid></Grid>
            <Grid><Typography variant="h6" color={theme.palette.secondary.main}>{t("approver.distributions")}</Typography></Grid>
            <Grid><IconButton disabled={props.onlyReasign} onClick={handleAddDistribution}><Add /></IconButton></Grid>
        </Grid>
        <Grid>
            <TableContainer sx={{ minHeight: "14rem", height: { lg: heightLg, xs: "14rem" },}}>
                <Table stickyHeader size="small" padding="normal">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("basic.debitAccount")}</TableCell>
                            <TableCell>{t("basic.debitVat")}</TableCell>
                            <TableCell>{t("search.amount")}</TableCell>
                            <TableCell>{t("search.department")}</TableCell>
                            <TableCell>{t("search.project")}</TableCell>
                            <TableCell>{t("")}</TableCell>
                        </TableRow>
                        <TableRow sx={{ visibility: "hidden" }}>
                            <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {distributions?.map((e, index) => {
                            return <SubRows document={props.document} setDocument={props.setDocument} index={index} organizationId={props.organizationId} />
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    </Grid>
}

export default DialogDocumentApproverDistributions;